import { InputNumber } from "../../../../../../../components/InputNumber/InputNumber.tsx";
import { SETTINGS_ITEM_WIDTH } from "../../constants.ts";

const MIN_FONT_SIZE = 8;
const MAX_FONT_SIZE = 144;

const FONT_SIZE_RANGES = [
  { start: MIN_FONT_SIZE, end: 16, step: 1 },
  { start: 18, end: 48, step: 2 },
  { start: 52, end: MAX_FONT_SIZE, step: 4 },
] as const;

const getTotalSteps = (): number =>
  FONT_SIZE_RANGES.reduce(
    (total, range) =>
      total + Math.floor((range.end - range.start) / range.step) + 1,
    0,
  );

const sliderValueToFontSize = (sliderValue: number): number => {
  const totalSteps = getTotalSteps();
  const clamped = Math.max(0, Math.min(totalSteps - 1, sliderValue));

  let currentStep = 0;

  for (const range of FONT_SIZE_RANGES) {
    const stepsInRange = Math.floor((range.end - range.start) / range.step) + 1;

    if (clamped < currentStep + stepsInRange) {
      const stepInRange = clamped - currentStep;
      return range.start + stepInRange * range.step;
    }

    currentStep += stepsInRange;
  }

  return MAX_FONT_SIZE;
};

const fontSizeToSliderValue = (fontSize: number): number => {
  if (fontSize <= MIN_FONT_SIZE) return 0;
  if (fontSize >= MAX_FONT_SIZE) return getTotalSteps() - 1;

  let currentStep = 0;

  for (const range of FONT_SIZE_RANGES) {
    if (fontSize >= range.start && fontSize <= range.end) {
      const stepInRange = Math.round((fontSize - range.start) / range.step);
      return currentStep + stepInRange;
    }

    const stepsInRange = Math.floor((range.end - range.start) / range.step) + 1;
    currentStep += stepsInRange;
  }

  return getTotalSteps() - 1;
};

export const FontSizeInputNumber = ({
  fontSize,
  onFontSizeChange,
}: {
  fontSize: number;
  onFontSizeChange: (fontSize: number) => void;
}) => {
  const onIconDrag = (deltaX: number, pixelRange: number) => {
    const pixelDelta = (deltaX / pixelRange) * (getTotalSteps() - 1);

    const clampedSliderValue = Math.max(
      0,
      Math.min(
        getTotalSteps() - 1,
        fontSizeToSliderValue(fontSize) + pixelDelta,
      ),
    );
    onFontSizeChange(Math.round(sliderValueToFontSize(clampedSliderValue)));
  };

  return (
    <InputNumber
      min={MIN_FONT_SIZE}
      max={MAX_FONT_SIZE}
      value={fontSize}
      onChange={onFontSizeChange}
      className={SETTINGS_ITEM_WIDTH}
      suffix="px"
      iconName="ALargeSmall"
      onIconDrag={(deltaX) => onIconDrag(deltaX, 100)}
    />
  );
};
